import React, { createContext, useContext, useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  // Check if user has a preferred theme saved in localStorage, default to light
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark';
  });
  // How many mounted screens currently suspend the legacy `dark-mode` class
  // (see useSuspendLegacyDarkMode below).
  const [legacyDarkSuspensions, setLegacyDarkSuspensions] = useState(0);
  // How many mounted screens are always dark (see useForceDarkTheme below)
  const [forcedDarkScreens, setForcedDarkScreens] = useState(0);

  // Apply theme classes. A layout effect so they are in place before paint;
  // otherwise a screen suspending the legacy class would flash with the legacy
  // overrides on mount, and the page after it would flash without them.
  useLayoutEffect(() => {
    const html = document.documentElement; // Get the html element
    const applyLegacyClass = isDarkMode && legacyDarkSuspensions === 0;

    // Legacy stylesheets key their dark styles off `dark-mode` on body/html
    document.body.classList.toggle('dark-mode', applyLegacyClass);
    html.classList.toggle('dark-mode', applyLegacyClass);
    // Tailwind's `dark:` variant keys off this class (tailwind.config.js;
    // Tailwind adds its `tw-` prefix to the configured `.dark` selector)
    html.classList.toggle('tw-dark', isDarkMode || forcedDarkScreens > 0);
  }, [isDarkMode, legacyDarkSuspensions, forcedDarkScreens]);

  // Persist the preference
  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Toggle between dark and light mode
  const toggleTheme = useCallback(() => {
    setIsDarkMode(prevMode => !prevMode);
  }, []);

  // Returns its own release function, so an effect can return it directly.
  const suspendLegacyDarkMode = useCallback(() => {
    setLegacyDarkSuspensions(count => count + 1);
    return () => setLegacyDarkSuspensions(count => count - 1);
  }, []);

  // Returns its own release function, like suspendLegacyDarkMode.
  const forceDarkTheme = useCallback(() => {
    setForcedDarkScreens(count => count + 1);
    return () => setForcedDarkScreens(count => count - 1);
  }, []);

  const contextValue = useMemo(
    () => ({ isDarkMode, toggleTheme, suspendLegacyDarkMode, forceDarkTheme }),
    [isDarkMode, toggleTheme, suspendLegacyDarkMode, forceDarkTheme]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Lifts the legacy `dark-mode` class off body/html while the calling component
 * is mounted. Legacy stylesheets hang hundreds of broad `body.dark-mode ...`
 * !important overrides on it (p, span, svg, button, [class*="card"], ...) that
 * would repaint a Tailwind screen, which styles dark mode through the `dark:`
 * variant instead. Meant for full-page screens: anything else rendered at the
 * same time loses its legacy dark styling while the class is lifted.
 */
export const useSuspendLegacyDarkMode = () => {
  const { suspendLegacyDarkMode } = useTheme();
  useLayoutEffect(() => suspendLegacyDarkMode(), [suspendLegacyDarkMode]);
};

/**
 * Shows Tailwind's dark theme while the calling screen is mounted, whatever
 * the user picked (the login screen is always dark). Covers dialogs and
 * toasts too, since they render outside the screen. The saved preference
 * isn't touched: the next screen is back in the user's theme.
 */
export const useForceDarkTheme = () => {
  const { forceDarkTheme } = useTheme();
  useLayoutEffect(() => forceDarkTheme(), [forceDarkTheme]);
};

export default ThemeProvider;
